import {
    DataTable,
    DateField,
    ExportButton,
    List,
    ReferenceField,
    TextField,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Calendar } from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { TopToolbar } from '../../layout/TopToolbar';
import { PermissionCreateButton } from '../shared/PermissionCreateButton';
import { StatusBadge } from '../shared/StatusBadge';

const PaymentCell = () => {
    const record = useRecordContext();
    if (!record) return null;
    let status = record.payment_status || record.status || 'draft';
    
    if (record.due_date && status !== 'paid' && status !== 'draft') {
        const dueDate = new Date(record.due_date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (dueDate < today) {
            status = 'overdue';
        }
    }

    return <StatusBadge status={status} />;
};

const AmountCell = () => {
    const record = useRecordContext();
    if (!record?.total_amount) return <span className="text-slate-400 text-xs">—</span>;
    return (
        <div className="flex items-center gap-1 font-bold text-green-700">
            {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.total_amount)}
        </div>
    );
};

const PaidIndicator = () => {
    const record = useRecordContext();
    if (!record) return null;
    const paid = record.paid_amount || 0;
    const total = record.total_amount || 1;
    const pct = Math.min(100, Math.round((paid / total) * 100));
    if (pct === 0) return <span className="text-xs text-slate-400">Not Paid</span>;
    return (
        <Badge variant="outline" className={`text-[10px] font-bold ${pct >= 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
            {pct}% Paid
        </Badge>
    );
};

const InvoiceListActions = () => (
    <TopToolbar>
        <ExportButton />
        <PermissionCreateButton permission="manage_documents" label="New Invoice" />
    </TopToolbar>
);

import { getGlobalFilters } from '../shared/GlobalFilterBar';

const invoiceStatusChoices = [
    { id: 'draft', name: 'Draft' },
    { id: 'approved', name: 'Approved' },
    { id: 'sent', name: 'Sent' },
    { id: 'paid', name: 'Paid' },
    { id: 'overdue', name: 'Overdue' },
    { id: 'due_soon', name: 'Due Soon (2d)' },
];

export const InvoiceList = () => (
    <List
        title="Invoices"
        actions={<InvoiceListActions />}
        filters={getGlobalFilters(invoiceStatusChoices, "Inv Status")}
        sort={{ field: 'date', order: 'DESC' }}
        className="p-6"
        filtersLayout="two-rows"
    >
        <Card className="border-none shadow-sm overflow-hidden">
            <DataTable rowClick="show">
                <DataTable.Col
                    source="reference"
                    label="Tax Invoice No."
                    className="font-mono font-bold text-slate-900"
                />
                <DataTable.Col label="Company">
                    <ReferenceField source="company_id" reference="companies" link={false}>
                        <TextField source="name" className="font-semibold text-slate-700" />
                    </ReferenceField>
                </DataTable.Col>
                <DataTable.Col source="customer_name" label="Customer" className="text-sm" />
                <DataTable.Col label="Date">
                    <div className="flex items-center gap-2 text-slate-500 text-xs">
                        <Calendar className="w-3 h-3" />
                        <DateField source="date" />
                    </div>
                </DataTable.Col>
                <DataTable.Col label="Payment">
                    <PaymentCell />
                </DataTable.Col>
                <DataTable.Col label="Paid">
                    <PaidIndicator />
                </DataTable.Col>
                <DataTable.Col label="Total">
                    <AmountCell />
                </DataTable.Col>
                <DataTable.Col label="Created">
                    <DateField source="created_at" showTime showDate />
                </DataTable.Col>
            </DataTable>
        </Card>
    </List>
);
